import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ForgotPasswordForm from '../../components/forgot-password/ForgotPasswordForm';
import ResetPasswordOtpForm from '../../components/reset-password-otp/ResetPasswordOtpForm';
import NewPasswordForm from '../../components/new-password/NewPasswordForm';
import { useBrand } from '../../hooks/useBrand';
import './ForgotPassword.css';

type Step = 'identifier' | 'otp' | 'new-password';

interface UserData {
  id: number;
  email: string;
}

const ForgotPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const application = searchParams.get('application');
  const [step, setStep] = useState<Step>('identifier');
  const [userData, setUserData] = useState<UserData | null>(null);
  const [otp, setOtp] = useState<string>('');
  const { brand, isBranded } = useBrand();

  useEffect(() => {
    document.title = isBranded
      ? `Reset your ${brand.name} password`
      : 'Reset your password | Nextek Systems';
  }, [brand.name, isBranded]);

  const handleForgotPasswordSuccess = (data: UserData) => {
    setUserData(data);
    setStep('otp');
  };

  const handleOtpSuccess = (verifiedOtp: string) => {
    setOtp(verifiedOtp);
    setStep('new-password');
  };

  const handleBack = () => {
    setStep('identifier');
    setUserData(null);
    setOtp('');
  };

  const renderStep = () => {
    switch (step) {
      case 'identifier':
        return <ForgotPasswordForm onSuccess={handleForgotPasswordSuccess} />;
      case 'otp':
        return userData && (
          <ResetPasswordOtpForm
            userData={userData}
            onSuccess={handleOtpSuccess}
            onBack={handleBack}
          />
        );
      case 'new-password':
        return userData && (
          <NewPasswordForm userData={userData} otp={otp} application={application} />
        );
      default:
        return <ForgotPasswordForm onSuccess={handleForgotPasswordSuccess} />;
    }
  };

  return (
    <div className="forgot-password-page">
      {/* Background decorations */}
      <div className="forgot-password-page__bg">
        <div className="forgot-password-page__blob forgot-password-page__blob--1" />
        <div className="forgot-password-page__blob forgot-password-page__blob--2" />
        <div className="forgot-password-page__blob forgot-password-page__blob--3" />
        <div className="forgot-password-page__grid" />
      </div>

      {/* Content */}
      <div className="forgot-password-page__content">
        {renderStep()}
      </div>

      {/* Footer */}
      <div className="forgot-password-page__footer">
        <div className="forgot-password-page__footer-wave">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path
              fill="rgba(255, 255, 255, 0.8)"
              d="M0,40 C320,100 420,20 720,50 C1020,80 1200,20 1440,60 L1440,100 L0,100 Z"
            />
          </svg>
        </div>
        <p className="forgot-password-page__copyright">
          &copy; {new Date().getFullYear()} Nextek Systems. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
